import {targets, useReferentialContext} from "../../Context";
import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {
    CompetitiveGames,
    CompetitiveRoundSummaries,
    CompetitiveTeamRoundSummaries,
    Team
} from "../../reckon-api";
import WindowContainer from "../../components/layout/WindowContainer";
import {CircularProgress, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import SpecificRoundSelector from "../../components/selectors/Competitive/SpecificRoundSelector";
import {MapRender, SmokeAbilityWithAgent, UtilityAbilityWithAgent, OtherAbilityWithAgent, KillWithAgent} from "../../components/Maps/MapRender";
import SpecificGameSelectorDialog from "../../components/selectors/Competitive/SpecificGameSelectorDialog";
import NeonDrawer from "../../NeonComponents/NeonDrawer";
import Box from "@mui/material/Box";
import {Skeleton} from "@mui/lab";
import { TeamScoreboard } from "./PlayerScoreboard";
import RoundEventsTimeline from "./RoundEventsTimeline";
import GameVodPlayer, { parseYoutubeVod } from "./GameVodPlayer";
import {Pause, PlayArrow, SkipNext, SkipPrevious} from "@mui/icons-material";

type RoundView = {
    summaries: CompetitiveRoundSummaries[];
    smokes: SmokeAbilityWithAgent[];
    utils: UtilityAbilityWithAgent[];
    grenades: OtherAbilityWithAgent[];
    others: OtherAbilityWithAgent[];
    ultimates: OtherAbilityWithAgent[];
    kills: KillWithAgent[];
    duration: number;
    roundStartTime?: number;
    plantTime?: number;
    plantLocation?: {x: number, y: number};
};

function isGrenadeSlot(slot?: string) {
    return /grenade/i.test(slot || "");
}

function isUltimateSlot(slot?: string) {
    return /ult/i.test(slot || "");
}

function withCaster<T>(item: T, summary: CompetitiveRoundSummaries): T & { agent: string; side: string; player: string } {
    return { ...item, agent: summary.agent || "", side: summary.side || "", player: summary.player || "" };
}

function uniqueCasts<T extends { agent: string; slot: string; cast_position?: { time_in_round?: number | null } }>(items: T[]) {
    const seen = new Set<string>();
    return items.filter((item) => {
        const key = `${item.agent}|${item.slot}|${item.cast_position?.time_in_round ?? ""}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

function parsePlantLocation(raw: unknown): {x: number, y: number} | undefined {
    try {
        const loc = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (loc && typeof loc.x === "number" && typeof loc.y === "number") {
            return {x: loc.x, y: loc.y};
        }
    } catch {
        return undefined;
    }
    return undefined;
}

function buildRoundView(summaries: CompetitiveRoundSummaries[]): RoundView {
    const duration = Math.max(0, ...summaries.map((summary) => summary.player_snapshots?.length ?? 0));
    const roundStartTime = summaries[0]?.freeze_time_end_timestamp as number | undefined;
    const planter = summaries.find((summary) => summary.plant_time != null);
    const plantedAt = planter?.plant_time;
    return {
        summaries,
        duration,
        roundStartTime,
        plantTime: plantedAt != null && roundStartTime != null
            ? plantedAt + roundStartTime
            : undefined,
        plantLocation: planter?.plant_location ? parsePlantLocation(planter.plant_location) : undefined,
        smokes: summaries.flatMap((summary) =>
            (summary.smoke_abilities || []).map((smoke) => withCaster(smoke, summary))
        ),
        utils: summaries.flatMap((summary) =>
            (summary.utility_abilities || []).map((util) => withCaster(util, summary))
        ),
        grenades: uniqueCasts<OtherAbilityWithAgent>(summaries.flatMap((summary) => [
            ...(summary.utility_abilities || []).filter((util) => isGrenadeSlot(util.slot)).map((util) => withCaster(util, summary)),
            ...(summary.other_abilities || []).filter((ability) => isGrenadeSlot(ability.slot)).map((ability) => withCaster(ability, summary)),
        ])),
        others: summaries.flatMap((summary) =>
            (summary.other_abilities || [])
                .filter((ability) => !isGrenadeSlot(ability.slot) && !isUltimateSlot(ability.slot))
                .map((ability) => withCaster(ability, summary))
        ),
        ultimates: summaries.flatMap((summary) =>
            (summary.other_abilities || [])
                .filter((ability) => isUltimateSlot(ability.slot))
                .map((ability) => withCaster(ability, summary))
        ),
        kills: summaries.flatMap((summary) =>
            (summary.death_event || []).map((death: any) => ({
                ...death,
                killer_agent: death.killer_agent || "",
                side: summary.side === "Attacker" ? "Defender" : "Attacker",
            }))
        ),
    };
}

function formatHms(seconds: number): string {
    const total = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

function parseClockInput(raw: string): number | null {
    const text = raw.trim();
    if (!text) return null;
    if (/^\d+(\.\d+)?$/.test(text)) return Number(text);
    const hms = text.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i);
    if (hms && hms[0]) {
        return Number(hms[1] || 0) * 3600 + Number(hms[2] || 0) * 60 + Number(hms[3] || 0);
    }
    const parts = text.split(":").map((part) => Number(part));
    if (parts.length < 2 || parts.length > 3 || parts.some((part) => !Number.isFinite(part))) return null;
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
}

function globalTimestampAtTimer(summaries: CompetitiveRoundSummaries[], timer: number): number {
    const positions = summaries.find((summary) => summary.positions?.length)?.positions;
    if (positions?.length) {
        const index = Math.max(0, Math.min(positions.length - 1, Math.round(timer)));
        const fromIndex = Number(positions[index]?.timestamp);
        if (Number.isFinite(fromIndex)) return fromIndex;
    }
    const start = Number(summaries[0]?.start_time_seconds);
    if (Number.isFinite(start)) return start + timer;
    return timer;
}

function roundStartGlobal(summaries: CompetitiveRoundSummaries[]): number | undefined {
    const summary = summaries[0];
    if (!summary) return undefined;
    const start = Number(summary.start_time_seconds);
    const freeze = Number(summary.freeze_time_end_timestamp);
    if (!Number.isFinite(start) || !Number.isFinite(freeze)) return undefined;
    return start + freeze;
}

function vodClockAtTimer(summaries: CompetitiveRoundSummaries[], timer: number, round1StartGlobal: number | undefined, vodSyncSeconds: number): number {
    const gameClock = globalTimestampAtTimer(summaries, timer);
    if (round1StartGlobal == null) return vodSyncSeconds;
    return vodSyncSeconds + (gameClock - round1StartGlobal);
}

export default function GameReviewer(props: {target: targets}){
    const [selection, updateSelection] = useState(null as CompetitiveGames | null);
    const context = useReferentialContext();
    const [team, setTeam] = useState(context.selectedTeam as Team|null);
    const [roundSelected, setRoundSelected] = useState(null as CompetitiveTeamRoundSummaries|null);
    const [rounds, setRounds] = useState([] as CompetitiveTeamRoundSummaries[]);
    const [timestamp, setTimestamp] = useState(0 as number | number[]);
    const [playing, setPlaying] = useState(false);
    const [speed, setSpeed] = useState(1 as 1 | 1.5);
    const [roundDuration, setRoundDuration] = useState(0);
    const [vodSyncSeconds, setVodSyncSeconds] = useState(0);
    const [vodSyncText, setVodSyncText] = useState("00:00:00");

    const commitVodSync = (raw: string) => {
        const parsed = parseClockInput(raw);
        if (parsed == null) {
            setVodSyncText(formatHms(vodSyncSeconds));
            return;
        }
        setVodSyncSeconds(parsed);
        setVodSyncText(formatHms(parsed));
    };

    useEffect(() => {
        setRoundSelected(null);
        setPlaying(false);
        setRounds([]);
        setTimestamp(0);
        const vod = parseYoutubeVod(selection?.vod_yt);
        const start = (vod?.gameStartSeconds ?? 0) + 30;
        setVodSyncSeconds(start);
        setVodSyncText(formatHms(start));
    }, [selection]);

    useEffect(() => {
        setTeam(context.selectedTeam);
    }, [context.selectedTeam]);

    useEffect(() => {
        if (!selection || !team) {
            setRounds([]);
            return;
        }
        context.reckonApi.competitiveTeamRoundSummaries.list({
            team1: team.id,
            game: selection.id,
            ordering: ['round_index'],
        }).then(setRounds);
    }, [selection, team, context.reckonApi.competitiveTeamRoundSummaries]);

    const roundPos = roundSelected ? rounds.findIndex((round) => round.round_index === roundSelected.round_index) : -1;

    const selectRound = (round: CompetitiveTeamRoundSummaries | null, keepPlaying = false) => {
        if (!keepPlaying) setPlaying(false);
        setRoundSelected(round);
    };

    const goRound = (delta: number, keepPlaying = false) => {
        if (roundPos < 0) return false;
        const next = rounds[roundPos + delta];
        if (!next) return false;
        selectRound(next, keepPlaying);
        return true;
    };

    useEffect(() => {
        if (!playing) return;
        const id = window.setInterval(() => {
            setTimestamp((current) => {
                const now = typeof current === "number" ? current : 0;
                if (roundDuration <= 0 || now >= roundDuration) return now;
                return now + 1;
            });
        }, 1000 / speed);
        return () => window.clearInterval(id);
    }, [playing, speed, roundDuration]);

    useEffect(() => {
        if (!playing || roundDuration <= 0) return;
        const now = typeof timestamp === "number" ? timestamp : 0;
        if (now < roundDuration) return;
        if (goRound(1, true)) {
            setTimestamp(0);
            return;
        }
        setPlaying(false);
    }, [playing, timestamp, roundDuration, roundPos, rounds]);

    const controlSx = {
        color: "rgba(255,255,255,0.85)",
        border: "1px solid rgba(255,255,255,0.16)",
        borderRadius: "8px",
        width: 34,
        height: 34,
        "&:hover": { background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.32)" },
        "&.Mui-disabled": { color: "rgba(255,255,255,0.22)", borderColor: "rgba(255,255,255,0.08)" },
    };
    const speedSx = (active: boolean) => ({
        minWidth: 42,
        height: 34,
        px: 1,
        borderRadius: "8px",
        border: "1px solid rgba(255,255,255,0.16)",
        color: active ? "#fff" : "rgba(255,255,255,0.7)",
        background: active ? "rgba(255,255,255,0.12)" : "transparent",
        fontWeight: 800,
        fontSize: "0.72rem",
        textTransform: "none" as const,
        cursor: "pointer",
        "&:hover": { background: "rgba(255,255,255,0.1)", borderColor: "rgba(255,255,255,0.32)" },
    });

    return <WindowContainer direction={'column'} spacing={1} sx={{height:'100%', maxHeight:'100%', minHeight: 0, overflow:'hidden', boxSizing:'border-box', pt: 1.5, px: 2, pb: 1}}>
        <Box sx={{flexShrink: 0}}>
            <SpecificGameSelectorDialog
                game={selection}
                setGame={updateSelection}
                target={props.target}
                team={team}
                setTeam={setTeam}
                leading={selection && team ? (
                    <NeonDrawer
                        buttonLabel={roundSelected ? `Round ${roundSelected.round_index}` : 'Select Round'}
                        width="420px"
                    >
                        <SpecificRoundSelector
                            game={selection}
                            team={team}
                            selectedRound={roundSelected}
                            updateSelection={selectRound}
                            single={true}
                            fill
                        />
                    </NeonDrawer>
                ) : null}
                center={
                    <Stack direction="row" spacing={0.6} alignItems="center">
                        <Tooltip title="Previous round" arrow>
                            <span>
                                <IconButton size="small" onClick={() => goRound(-1)} disabled={roundPos <= 0} sx={controlSx}>
                                    <SkipPrevious sx={{ fontSize: 20 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Play" arrow>
                            <span>
                                <IconButton size="small" onClick={() => setPlaying(true)} disabled={!roundSelected} sx={{ ...controlSx, ...(playing ? { background: "rgba(255,255,255,0.12)", borderColor: "rgba(255,255,255,0.4)" } : {}) }}>
                                    <PlayArrow sx={{ fontSize: 20 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Pause" arrow>
                            <span>
                                <IconButton size="small" onClick={() => setPlaying(false)} disabled={!roundSelected} sx={{ ...controlSx, ...(!playing && roundSelected ? { background: "rgba(255,255,255,0.08)" } : {}) }}>
                                    <Pause sx={{ fontSize: 20 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Box component="button" onClick={() => setSpeed(1)} sx={speedSx(speed === 1)}>x1</Box>
                        <Box component="button" onClick={() => setSpeed(1.5)} sx={speedSx(speed === 1.5)}>x1.5</Box>
                        <Tooltip title="Video time when round 1 freeze ends. Defaults to the VOD t= plus 30 seconds." arrow>
                            <Box
                                component="input"
                                value={vodSyncText}
                                placeholder="hh:mm:ss"
                                disabled={!selection?.vod_yt}
                                onChange={(event) => setVodSyncText(event.target.value)}
                                onBlur={(event) => commitVodSync(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                        event.currentTarget.blur();
                                    }
                                }}
                                sx={{
                                    width: 88,
                                    height: 34,
                                    px: 1,
                                    borderRadius: "8px",
                                    border: "1px solid rgba(255,255,255,0.16)",
                                    background: "transparent",
                                    color: "rgba(255,255,255,0.9)",
                                    fontWeight: 800,
                                    fontSize: "0.72rem",
                                    fontFamily: "monospace",
                                    textAlign: "center",
                                    outline: "none",
                                    "&:hover": { borderColor: "rgba(255,255,255,0.32)" },
                                    "&:focus": { borderColor: "rgba(255,255,255,0.55)" },
                                    "&:disabled": { color: "rgba(255,255,255,0.22)", borderColor: "rgba(255,255,255,0.08)" },
                                }}
                            />
                        </Tooltip>
                        <Tooltip title="Next round" arrow>
                            <span>
                                <IconButton size="small" onClick={() => goRound(1)} disabled={roundPos < 0 || roundPos >= rounds.length - 1} sx={controlSx}>
                                    <SkipNext sx={{ fontSize: 20 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                }
            />
        </Box>
        <Box sx={{flex: 1, minHeight: 0, height: '100%', display: 'flex', overflow: 'hidden'}}>
            {roundSelected && selection ? (
                <RoundReview
                    round={roundSelected}
                    map_id={selection.map}
                    vodUrl={selection.vod_yt}
                    vodSyncSeconds={vodSyncSeconds}
                    timestamp={timestamp}
                    setTimestamp={setTimestamp}
                    playing={playing}
                    speed={speed}
                    onDuration={setRoundDuration}
                />
            ) : <Typography>Select a game</Typography>}
        </Box>
    </WindowContainer>
}

function RoundReview(props: {
    round: CompetitiveTeamRoundSummaries,
    map_id: string,
    vodUrl?: string | null,
    vodSyncSeconds: number,
    timestamp: number | number[],
    setTimestamp: React.Dispatch<React.SetStateAction<number | number[]>>,
    playing: boolean,
    speed: number,
    onDuration: (duration: number) => void,
}){
    const [render, setRender] = useState(true);
    const { timestamp, setTimestamp, onDuration } = props;
    const [playerRoundSummaries, setPlayerRoundSummaries] = useState([] as CompetitiveRoundSummaries[]);
    const ctx = useReferentialContext();
    const [roundDurationSeconds, setRoundDurationSeconds] = useState(0);
    const [roundStartTime, setRoundStartTime] = useState(undefined as undefined|number);
    const [plantTime, setPlantTime] = useState(undefined as undefined|number);
    const [plantLocation, setPlantLocation] = useState(undefined as undefined|{x: number, y: number});
    const [roundSmokes, setRoundSmokes] = useState([] as SmokeAbilityWithAgent[]);
    const [roundUtils, setRoundUtils] = useState([] as UtilityAbilityWithAgent[]);
    const [roundGrenades, setRoundGrenades] = useState([] as OtherAbilityWithAgent[]);
    const [roundOthers, setRoundOthers] = useState([] as OtherAbilityWithAgent[]);
    const [roundUltimates, setRoundUltimates] = useState([] as OtherAbilityWithAgent[]);
    const [roundKills, setRoundKills] = useState([] as KillWithAgent[]);
    const [loadingRound, setLoadingRound] = useState(false);
    const [map, setMap] = useState(ctx.mapsReferential.filter(m => m.id === props.map_id)[0]);
    const [round1StartGlobal, setRound1StartGlobal] = useState(undefined as number | undefined);
    const lastGameRef = useRef(props.round.game);
    const cacheRef = useRef(new Map<string, RoundView>());
    const inflightRef = useRef(new Map<string, Promise<RoundView>>());

    const applyView = (view: RoundView) => {
        setPlayerRoundSummaries(view.summaries);
        setRoundDurationSeconds(view.duration);
        onDuration(Math.max(view.duration - 1, 0));
        setRoundStartTime(view.roundStartTime);
        setTimestamp(Math.max(0, (view.roundStartTime ?? 0) - 5));
        setPlantTime(view.plantTime);
        setPlantLocation(view.plantLocation);
        setRoundSmokes(view.smokes);
        setRoundUtils(view.utils);
        setRoundGrenades(view.grenades);
        setRoundOthers(view.others);
        setRoundUltimates(view.ultimates);
        setRoundKills(view.kills);
        setRender(true);
        setLoadingRound(false);
        if (props.round.round_index === 1) {
            const start = roundStartGlobal(view.summaries);
            if (start != null) setRound1StartGlobal(start);
        }
    };

    useEffect(() => {
        const next = ctx.mapsReferential.find((m) => m.id === props.map_id);
        if (next) setMap(next);
    }, [props.map_id, ctx.mapsReferential]);

    // Round identity is the only trigger; applyView just batches setState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        const game = props.round.game;
        const roundIndex = props.round.round_index;
        if (lastGameRef.current !== game) {
            lastGameRef.current = game;
            setRound1StartGlobal(undefined);
        }
        const key = `${game}:${roundIndex}`;
        let cancelled = false;
        const summariesApi = ctx.reckonApi.competitiveRoundSummaries;

        const loadRound = (targetIndex: number) => {
            const targetKey = `${game}:${targetIndex}`;
            const cachedRound = cacheRef.current.get(targetKey);
            if (cachedRound) return Promise.resolve(cachedRound);
            const inflight = inflightRef.current.get(targetKey);
            if (inflight) return inflight;
            const request = summariesApi.list({
                round_index: targetIndex,
                game,
                ordering: ["team1"],
            }).then((summaries) => {
                const view = buildRoundView(summaries);
                if (summaries.length) cacheRef.current.set(targetKey, view);
                inflightRef.current.delete(targetKey);
                return view;
            }).catch((error) => {
                inflightRef.current.delete(targetKey);
                throw error;
            });
            inflightRef.current.set(targetKey, request);
            return request;
        };

        const cached = cacheRef.current.get(key);
        if (cached) {
            applyView(cached);
        } else {
            setLoadingRound(true);
            loadRound(roundIndex).then((view) => {
                if (!cancelled && view.summaries.length) applyView(view);
                else if (!cancelled) setLoadingRound(false);
            });
        }
        loadRound(1).then((view) => {
            const start = roundStartGlobal(view.summaries);
            if (!cancelled && start != null) setRound1StartGlobal(start);
        }).catch(() => undefined);
        if (roundIndex > 1) loadRound(roundIndex - 1).catch(() => undefined);
        loadRound(roundIndex + 1).catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [props.round.game, props.round.round_index]);

    useEffect(() => {
        setRender(false);
    }, [props.round.game]);

    const vod = parseYoutubeVod(props.vodUrl);
    const vodClock = vodClockAtTimer(playerRoundSummaries, Number(timestamp) || 0, round1StartGlobal, props.vodSyncSeconds);

    return <Stack direction={'row'} sx={{width:'100%', height:'100%', minHeight: 0, overflow:'hidden', position:'relative'}} spacing={1}>
        <Box sx={{
            flex: 1,
            minWidth: 280,
            minHeight: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            containerType: 'size',
        }}>
            <Box sx={{
                flex: '0 1 auto',
                minHeight: 0,
                pt: 0.5,
                overflow: 'hidden',
                maxHeight: vod ? 'calc(100cqh - (100cqw * 9 / 16) - 8px)' : '100%',
            }}>
                <RoundEventsTimeline
                    smokes={roundSmokes}
                    kills={roundKills}
                    utils={roundUtils.filter((util) => !isGrenadeSlot(util.slot))}
                    grenades={roundGrenades}
                    others={roundOthers}
                    ultimates={roundUltimates}
                    setTimestamp={setTimestamp}
                    currentTimestamp={timestamp as number}
                    plantTime={plantTime}
                    freezeTimeEnd={roundStartTime}
                    roundDuration={roundDurationSeconds - 1}
                />
            </Box>
            {vod ? (
                <Box
                    sx={{
                        width: "100%",
                        aspectRatio: "16 / 9",
                        height: "auto",
                        flexShrink: 0,
                        mt: 1,
                        borderRadius: "10px",
                        overflow: "hidden",
                        background: "rgba(10, 10, 20, 0.82)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
                    }}
                >
                    <GameVodPlayer
                        videoId={vod.videoId}
                        videoTime={vodClock}
                        playing={props.playing}
                        speed={props.speed}
                    />
                </Box>
            ) : null}
        </Box>
        <Box sx={{flexShrink: 0, height: '100%', display: 'flex', alignItems: 'stretch', minWidth: 0}}>
            <Box sx={{height: '100%', aspectRatio: '1 / 1', maxHeight: '100%', minWidth: 0}}>
                {render && map ?
                    <MapRender id={props.map_id} map={map} smokes={roundSmokes.filter(smoke => {
                        const ts = timestamp as number;
                        const smokeTime = smoke.cast_position?.time_in_round ?? 0;
                        return smoke.landing_point && ts > smokeTime && ts < (smokeTime + smoke.duration)
                    })}
                        playerPositions={playerRoundSummaries.map((prs) => {
                            return {round: prs, position:prs.positions? prs.positions[timestamp as number +1] : undefined }
                        })}
                        kills={roundKills.filter(kill => {
                            const ts = timestamp as number;
                            return kill.time_in_round < ts
                        })}
                       utils={roundUtils.filter(util => {
                           const ts = timestamp as number;
                           const utilTime = util.cast_position?.time_in_round ?? 0;
                           return util.landing_point && ts > utilTime
                       })}
                       spikePosition={plantLocation && plantTime !== undefined && (timestamp as number) >= plantTime ? plantLocation : undefined}
                    />
                    :
                    <Skeleton variant="rectangular" width="100%" height="100%" />
                }
            </Box>
            <Stack justifyContent="space-between" sx={{ml: 1, mr: 0.5, my: 1, flexShrink: 0, height: '100%', boxSizing: 'border-box'}}>
                <TeamScoreboard timestamp={timestamp as number} players={playerRoundSummaries.slice(0, 5)} align="right"/>
                <TeamScoreboard timestamp={timestamp as number} players={playerRoundSummaries.slice(5, 10)} align="right"/>
            </Stack>
        </Box>
        {loadingRound ? (
            <Box sx={{position:'absolute', inset: 0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none', zIndex: 6, background:'rgba(0,0,0,0.12)'}}>
                <CircularProgress size={28} />
            </Box>
        ) : null}
    </Stack>
}
